import { useState, useEffect } from 'react';
import { PageHeader, Card, LoadingSpinner, StatusBadge } from '../../components/Layout';
import api from '../../services/api';

export function RewardsManagementPage() {
  const [loading, setLoading] = useState(true);
  const [catalog, setCatalog] = useState<any[]>([]);
  const [redemptions, setRedemptions] = useState<any[]>([]);
  const [leaderboard, setLeaderboard] = useState<any[]>([]);
  const [showRewardForm, setShowRewardForm] = useState(false);
  const [showAwardPointsForm, setShowAwardPointsForm] = useState(false);
  const [editingReward, setEditingReward] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [employees, setEmployees] = useState<any[]>([]);
  const [rewardFormData, setRewardFormData] = useState({
    name: '',
    description: '',
    category: '',
    points_cost: '',
    quantity_available: '',
    is_active: true,
  });
  const [pointsFormData, setPointsFormData] = useState({
    employee_id: '',
    points: '',
    reason: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [catalogData, redemptionsData, leaderboardData, employeesData] = await Promise.all([
        api.getRewardsCatalog(),
        api.getAllRedemptions({ status: 'pending' }),
        api.getRewardsLeaderboard({ limit: 10 }),
        api.getAllEmployees(),
      ]);
      setCatalog(catalogData || []);
      setRedemptions(redemptionsData || []);
      setLeaderboard(leaderboardData || []);
      setEmployees(employeesData || []);
    } catch (error) {
      console.error('Error loading rewards data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReward = () => {
    setEditingReward(null);
    setRewardFormData({
      name: '',
      description: '',
      category: '',
      points_cost: '',
      quantity_available: '',
      is_active: true,
    });
    setShowRewardForm(true);
  };

  const handleEditReward = (reward: any) => {
    setEditingReward(reward);
    setRewardFormData({
      name: reward.name || '',
      description: reward.description || '',
      category: reward.category || '',
      points_cost: reward.points_cost?.toString() || '',
      quantity_available: reward.quantity_available?.toString() || '',
      is_active: reward.is_active !== undefined ? reward.is_active : true,
    });
    setShowRewardForm(true);
  };

  const handleSaveReward = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!rewardFormData.name.trim() || !rewardFormData.points_cost) {
      alert('Name and points cost are required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...rewardFormData,
        points_cost: parseInt(rewardFormData.points_cost),
        quantity_available: rewardFormData.quantity_available ? parseInt(rewardFormData.quantity_available) : null,
      };

      if (editingReward) {
        await api.updateReward(editingReward.id, payload);
        alert('Reward updated successfully');
      } else {
        await api.createReward(payload);
        alert('Reward created successfully');
      }

      setShowRewardForm(false);
      loadData();
    } catch (error: any) {
      alert(error.message || 'Failed to save reward');
    } finally {
      setSaving(false);
    }
  };

  const handleAwardPoints = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pointsFormData.employee_id || !pointsFormData.points || !pointsFormData.reason) {
      alert('All fields are required');
      return;
    }

    setSaving(true);
    try {
      await api.awardBonusPoints({
        employee_id: pointsFormData.employee_id,
        points: parseInt(pointsFormData.points),
        reason: pointsFormData.reason,
      });
      alert('Points awarded successfully');
      setShowAwardPointsForm(false);
      setPointsFormData({ employee_id: '', points: '', reason: '' });
      loadData();
    } catch (error: any) {
      alert(error.message || 'Failed to award points');
    } finally {
      setSaving(false);
    }
  };

  const handleFulfillRedemption = async (id: string) => {
    const notes = prompt('Enter fulfillment notes (optional):');
    try {
      await api.fulfillRedemption(id, notes || '');
      alert('Redemption fulfilled successfully');
      loadData();
    } catch (error: any) {
      alert(error.message || 'Failed to fulfill redemption');
    }
  };

  const handleCancelRedemption = async (id: string) => {
    if (!confirm('Cancel this redemption? Points will be restored to the employee.')) return;
    try {
      await api.cancelRedemption(id);
      alert('Redemption cancelled');
      loadData();
    } catch (error: any) {
      alert(error.message || 'Failed to cancel redemption');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Rewards Management"
        subtitle="Manage employee rewards program, catalog, and point system"
        actions={
          <div className="flex gap-3">
            <button
              onClick={() => setShowAwardPointsForm(true)}
              className="px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
            >
              Award Points
            </button>
            <button
              onClick={handleCreateReward}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Add Reward
            </button>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Pending Redemptions */}
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Pending Redemptions</h2>
          {redemptions.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No pending redemptions</p>
          ) : (
            <div className="space-y-3">
              {redemptions.map((redemption) => (
                <div key={redemption.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-medium text-gray-900">{redemption.employee_name}</div>
                      <div className="text-sm text-gray-600">{redemption.reward_name}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-blue-600">{redemption.points_spent} pts</div>
                      <div className="text-xs text-gray-500">
                        {new Date(redemption.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => handleFulfillRedemption(redemption.id)}
                      className="flex-1 px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                    >
                      Fulfill
                    </button>
                    <button
                      onClick={() => handleCancelRedemption(redemption.id)}
                      className="flex-1 px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Leaderboard */}
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Points Leaderboard</h2>
          {leaderboard.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No leaderboard data</p>
          ) : (
            <div className="space-y-2">
              {leaderboard.map((entry, index) => (
                <div key={entry.employee_id} className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                      index === 0 ? 'bg-yellow-100 text-yellow-700' :
                      index === 1 ? 'bg-gray-100 text-gray-700' :
                      index === 2 ? 'bg-orange-100 text-orange-700' :
                      'bg-gray-50 text-gray-600'
                    }`}>
                      {index + 1}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">{entry.employee_name}</div>
                      <div className="text-xs text-gray-500">{entry.department}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-blue-600">{entry.total_points} pts</div>
                    <div className="text-xs text-gray-500 capitalize">{entry.tier}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Rewards Catalog */}
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Rewards Catalog</h2>
        {catalog.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No rewards in catalog</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Name</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Category</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Points</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Quantity</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Status</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {catalog.map((reward) => (
                  <tr key={reward.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{reward.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{reward.category || '--'}</td>
                    <td className="px-4 py-3 text-sm text-blue-600 font-semibold">{reward.points_cost}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {reward.quantity_available !== null ? reward.quantity_available : 'Unlimited'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {reward.is_active ? (
                        <span className="text-green-600 font-medium">Active</span>
                      ) : (
                        <span className="text-gray-500">Inactive</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-right space-x-2">
                      <button
                        onClick={() => handleEditReward(reward)}
                        className="text-blue-600 hover:text-blue-700 font-medium"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Reward Form Modal */}
      {showRewardForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {editingReward ? 'Edit Reward' : 'Add Reward'}
            </h2>
            <form onSubmit={handleSaveReward} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reward Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={rewardFormData.name}
                  onChange={(e) => setRewardFormData({ ...rewardFormData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={rewardFormData.description}
                  onChange={(e) => setRewardFormData({ ...rewardFormData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={rewardFormData.category}
                    onChange={(e) => setRewardFormData({ ...rewardFormData, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select category...</option>
                    <option value="Gift Cards">Gift Cards</option>
                    <option value="Electronics">Electronics</option>
                    <option value="Time Off">Time Off</option>
                    <option value="Merchandise">Merchandise</option>
                    <option value="Experiences">Experiences</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Points Cost <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={rewardFormData.points_cost}
                    onChange={(e) => setRewardFormData({ ...rewardFormData, points_cost: e.target.value })}
                    onWheel={(e) => e.currentTarget.blur()}
                    min="1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quantity Available
                </label>
                <input
                  type="number"
                  value={rewardFormData.quantity_available}
                  onChange={(e) => setRewardFormData({ ...rewardFormData, quantity_available: e.target.value })}
                  onWheel={(e) => e.currentTarget.blur()}
                  min="0"
                  placeholder="Leave empty for unlimited"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="reward_is_active"
                  checked={rewardFormData.is_active}
                  onChange={(e) => setRewardFormData({ ...rewardFormData, is_active: e.target.checked })}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="reward_is_active" className="ml-2 text-sm font-medium text-gray-700">
                  Active (available for redemption)
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowRewardForm(false)}
                  disabled={saving}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : editingReward ? 'Update Reward' : 'Add Reward'}
                </button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {/* Award Points Form Modal */}
      {showAwardPointsForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="max-w-md w-full">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Award Bonus Points</h2>
            <form onSubmit={handleAwardPoints} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Employee <span className="text-red-500">*</span>
                </label>
                <select
                  value={pointsFormData.employee_id}
                  onChange={(e) => setPointsFormData({ ...pointsFormData, employee_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select employee...</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Points <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={pointsFormData.points}
                  onChange={(e) => setPointsFormData({ ...pointsFormData, points: e.target.value })}
                  onWheel={(e) => e.currentTarget.blur()}
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={pointsFormData.reason}
                  onChange={(e) => setPointsFormData({ ...pointsFormData, reason: e.target.value })}
                  rows={3}
                  placeholder="e.g., Exceptional performance on Project X"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowAwardPointsForm(false)}
                  disabled={saving}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? 'Awarding...' : 'Award Points'}
                </button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
}
